/**
 * DecryptedRotor
 *
 * A single-line auto-cycling tagline that runs the cyber-cipher decrypt
 * animation, holds the resolved text for a moment, then transitions to
 * the next tagline. Designed for the homepage hero to hammer the
 * twin-pillar narrative on every visit.
 *
 * Props:
 *   - taglines    (string[])  — sequence of taglines to cycle through.
 *   - holdMs      (number)    — how long to hold each resolved tagline. Default 4200.
 *   - revealMs    (number)    — duration of the cipher → resolved animation. Default 1800.
 *   - className   (string)    — className for the outer line.
 *   - testId      (string)    — data-testid on the line.
 *   - accentColor (string)    — accent color used by the cipher chars. Default cyan.
 */
import React, { useEffect, useState } from "react";
import useDecryptionReveal from "../../lib/useDecryptionReveal";


export default function DecryptedRotor({
  taglines,
  holdMs = 4200,
  revealMs = 1800,
  className = "",
  testId,
  accentColor = "var(--qt-cyan)",
}) {
  const [idx, setIdx] = useState(0);
  const target = taglines[idx % taglines.length];
  const { text, lockedFlags } = useDecryptionReveal(target, revealMs);

  // After the reveal finishes, hold for `holdMs` then advance to the
  // next tagline. The hook re-runs on `target` change, so simply
  // updating `idx` re-fires the animation.
  useEffect(() => {
    if (taglines.length <= 1) return undefined;
    const total = revealMs + holdMs;
    const t = setTimeout(() => {
      setIdx((i) => (i + 1) % taglines.length);
    }, total);
    return () => clearTimeout(t);
  }, [idx, taglines.length, revealMs, holdMs]);

  const display = text || target || "";

  return (
    <span
      className={`qt-decrypt-rotor ${className}`}
      data-testid={testId}
      style={{
        ["--qt-rotor-accent"]: accentColor,
      }}
    >
      <span
        className="qt-decrypt-rotor-line"
        role="status"
        aria-live="polite"
        aria-label={target}
      >
        {splitIntoWords(display).map((word, wIdx) => (
          <React.Fragment key={`w-${wIdx}`}>
            <span className="qt-decrypt-word">
              {word.chars.map((entry, i) => {
                const flagIdx = entry.absIdx;
                const isResolved =
                  lockedFlags[flagIdx] === false &&
                  flagIdx < (target?.length || 0) &&
                  entry.ch === target?.[flagIdx];
                const justLocked = lockedFlags[flagIdx] === true;
                const cls = justLocked
                  ? "qt-rotor-ch is-flash"
                  : isResolved
                    ? "qt-rotor-ch is-resolved"
                    : "qt-rotor-ch is-cipher";
                return (
                  <span key={i} className={cls} aria-hidden="true">
                    {entry.ch}
                  </span>
                );
              })}
            </span>
            {word.trailingSpace ? " " : null}
          </React.Fragment>
        ))}
      </span>
    </span>
  );
}


function splitIntoWords(displayStr) {
  const out = [];
  const src = displayStr || "";
  let i = 0;
  while (i < src.length) {
    while (i < src.length && src[i] === " ") {
      out.push({ chars: [], trailingSpace: true });
      i += 1;
    }
    if (i >= src.length) break;
    const chars = [];
    while (i < src.length && src[i] !== " ") {
      chars.push({ ch: src[i], absIdx: i });
      i += 1;
    }
    const trailingSpace = i < src.length && src[i] === " ";
    if (trailingSpace) i += 1;
    out.push({ chars, trailingSpace });
  }
  return out;
}
